// src/pages/Events.js — Logged events (conferences, dinners, roadshows...),
// newest first. Each one has its own page (EventDetail.js) for the agenda
// and attendees.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useIsPreview } from '../lib/preview'
import { EntityField } from '../components/EntityPicker'
import { dateRange, oneLine } from '../lib/searchTypes'

export function EventModal({ title, onClose, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
        width: '100%', maxWidth: 600, maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

// Kinds of event; keep in step with EVENT_TYPES in src/routes/events.js.
export const EVENT_TYPES = ['Conference', 'Dinner', 'Roadshow', 'Webinar', 'Other']

const EMPTY = { event_type: 'Conference', name: '', organizer_firm_id: '', start_date: '', end_date: '', location: '', website: '', notes: '' }

// Chip input for an event's tags (stored in events.topics). Suggestions are the CRM's own vocabulary
// ({ label, group }: investor types, firm/contact types, taxonomy terms), so
// an event is tagged the way firms are; anything typed still works.
export function TopicInput({ topics, onChange, suggestions }) {
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const lower = topics.map(t => t.toLowerCase())
  const options = useMemo(() => {
    const q = input.trim().toLowerCase()
    return suggestions.filter(s => !lower.includes(s.label.toLowerCase()) &&
      (!q || s.label.toLowerCase().includes(q) || s.group.toLowerCase().includes(q))).slice(0, 12)
  }, [input, suggestions, lower])

  function add(t) {
    const v = t.trim()
    if (v && !lower.includes(v.toLowerCase())) onChange([...topics, v])
    setInput('')
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input className="input" value={input} placeholder="Type a tag and press Enter"
          onChange={e => { setInput(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ',') && input.trim()) { e.preventDefault(); add(input) }
            if (e.key === 'Backspace' && !input && topics.length) onChange(topics.slice(0, -1))
          }} />
        {open && options.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 2,
            background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 6,
            maxHeight: 200, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {options.map(o => (
              <div key={o.label} onMouseDown={() => add(o.label)}
                style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12 }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-muted)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                <span>{o.label}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{o.group}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {topics.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          {topics.map(t => (
            <span key={t} className="badge badge-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t}
              <span onClick={() => onChange(topics.filter(x => x !== t))}
                style={{ cursor: 'pointer', opacity: 0.7, fontWeight: 700 }}>×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// Tag suggestions: the CRM's vocabulary, [{ label, group }].
export function useTagOptions() {
  const { data } = useQuery({
    queryKey: ['event-topics'],
    queryFn: () => api.get('/api/events/topics').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  return data ?? []
}

// Create and edit.
export function EventForm({ initial, onSubmit, onCancel, submitting, error }) {
  const [form, setForm] = useState(() => {
    const f = { ...EMPTY }
    for (const k of Object.keys(EMPTY)) if (initial?.[k] != null) f[k] = initial[k]
    return f
  })
  const [topics, setTopics] = useState(() => initial?.topics ?? [])
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const suggestions = useTagOptions()

  function submit(e) {
    e.preventDefault()
    const payload = { topics }
    for (const k of Object.keys(EMPTY)) payload[k] = form[k].trim() ? form[k].trim() : null
    onSubmit(payload)
  }

  return (
    <form onSubmit={submit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div>
          <label className="input-label">Title *</label>
          <input className="input" value={form.name} onChange={e => set('name', e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="input-label">Type</label>
          <select className="select" value={form.event_type} onChange={e => set('event_type', e.target.value)}>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="input-label">Organizer</label>
        <EntityField kind="firm" value={form.organizer_firm_id} onChange={id => set('organizer_firm_id', id ?? '')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="input-label">Start date</label>
          <input className="input" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
        </div>
        <div>
          <label className="input-label">End date</label>
          <input className="input" type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
        </div>
      </div>
      <div>
        <label className="input-label">Location</label>
        <textarea className="textarea" rows={4} value={form.location} onChange={e => set('location', e.target.value)}
          placeholder={'Venue\nAddress line 1\nAddress line 2\nCity, State Zip'} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          As on a letter, one line each: venue, address, suite or floor, city, state and zip.
        </div>
      </div>
      <div>
        <label className="input-label">Website</label>
        <input className="input" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" />
      </div>
      <div>
        <label className="input-label">Tags</label>
        <TopicInput topics={topics} onChange={setTopics} suggestions={suggestions} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          One per audience or subject, from the CRM's own vocabulary: investor types (Pension Fund, Family Office...),
          LP / GP, and the investment taxonomy (Private Equity, Secondaries, Healthcare & Life Sciences...). Type to filter.
        </div>
      </div>
      <div>
        <label className="input-label">Notes</label>
        <textarea className="textarea" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Attach the agenda on the event's page once it's saved.
        </div>
      </div>
      {error && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting || !form.name.trim()}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function EventTable({ rows, navigate }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr><th>Event</th><th>Type</th><th>Organizer</th><th>Dates</th><th>Location</th><th>Attendees</th></tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.event_id} onClick={() => navigate(`/events/${c.event_id}`)} style={{ cursor: 'pointer' }}>
              <td style={{ fontWeight: 500 }}>{c.name}</td>
              <td><span className="badge badge-blue">{c.event_type}</span></td>
              <td>{c.organizer || '—'}</td>
              <td>{dateRange(c.start_date, c.end_date)}</td>
              <td>{oneLine(c.location) || '—'}</td>
              <td>{c.attendee_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Conference tiles (preview) ────────────────────────────────────────────
// Face tiles in the Dashboard's mandate-tile style: title, organizer, date.
// Click opens the event's detail page.
const SECTION_HEADING_STYLE = {
  fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-secondary)', margin: '0 0 12px',
}

function ConferenceTile({ event }) {
  const navigate = useNavigate()
  const open = () => navigate(`/events/${event.event_id}`)
  return (
    <div
      className="card-hover"
      onClick={open}
      role="link"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } }}
      style={{
        display: 'flex', flexDirection: 'column', minHeight: 140,
        background: 'var(--surface)', border: '1px solid var(--tile-outline)', borderRadius: '8px',
        padding: '16px 20px', cursor: 'pointer',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 17, color: 'var(--text-primary)', lineHeight: 1.35, marginBottom: 8 }}>
        {event.name}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {event.organizer || '—'}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 'auto' }}>
        {dateRange(event.start_date, event.end_date) || 'Date TBD'}
      </div>
    </div>
  )
}

// Newest first; undated events last.
const byDateDesc = (a, b) => (b.start_date || '').localeCompare(a.start_date || '')

function ConferenceTiles({ events }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={SECTION_HEADING_STYLE}>Conferences</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {[...events].sort(byDateDesc).map(e => <ConferenceTile key={e.event_id} event={e} />)}
      </div>
    </section>
  )
}

export default function Events() {
  const isPreview = useIsPreview()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  // Tiles or table (preview), remembered per browser. Same Board / Table
  // buttons as the mandate page's investor pipeline.
  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem('events-view') === 'table' ? 'table' : 'tiles' } catch { return 'tiles' }
  })
  function setView(v) {
    setViewState(v)
    try { localStorage.setItem('events-view', v) } catch { /* private window */ }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get('/api/events').then(r => r.data.data ?? []),
  })

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/events', payload).then(r => r.data.data),
    onSuccess: ev => {
      qc.invalidateQueries({ queryKey: ['events'] })
      navigate(`/events/${ev.event_id}`)
    },
  })

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data ?? [])
      .filter(e => !typeFilter || e.event_type === typeFilter)
      .filter(e => !q || [e.name, e.organizer, e.location, ...(e.topics ?? [])].some(v => (v || '').toLowerCase().includes(q)))
  }, [data, search, typeFilter])

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Events</h1>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ New Event</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by title, organizer, location or tag..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="select" style={{ maxWidth: 180, fontSize: 12 }} value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {isPreview && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <button className={`btn btn-sm ${view === 'tiles' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView('tiles')}>Tiles</button>
            <button className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView('table')}>Table</button>
          </div>
        )}
      </div>

      {error ? (
        <div className="empty-state"><h3>Could not load events</h3><p>{error.response?.data?.error || error.message}</p></div>
      ) : isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
      ) : !rows.length ? (
        <div className="empty-state" style={{ paddingTop: 40 }}>
          <h3>{data?.length ? 'No matches' : 'No events yet'}</h3>
          <p>{data?.length ? 'Try a different term or type.' : 'Log one with + New Event, then attach its agenda and add attendees.'}</p>
        </div>
      ) : isPreview && view === 'tiles' ? (
        <>
          {rows.some(e => e.event_type === 'Conference') && (
            <ConferenceTiles events={rows.filter(e => e.event_type === 'Conference')} />
          )}
          {rows.some(e => e.event_type !== 'Conference') && (
            <section>
              <h2 style={SECTION_HEADING_STYLE}>Other events</h2>
              <EventTable rows={[...rows.filter(e => e.event_type !== 'Conference')].sort(byDateDesc)} navigate={navigate} />
            </section>
          )}
        </>
      ) : (
        <EventTable rows={rows} navigate={navigate} />
      )}

      {createOpen && (
        <EventModal title="New Event" onClose={() => setCreateOpen(false)}>
          <EventForm
            onSubmit={createMutation.mutate}
            onCancel={() => setCreateOpen(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </EventModal>
      )}
    </div>
  )
}
